import { Component, Inject, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Maintenance, MaintenanceMaterial, MaintenanceRecurrenceType, MaintenanceRegieLine } from '../../../../Models/maintenance.model';
import { Customer } from '../../../../Models/customer.model';
import { MaintenanceChecklist } from '../../../../Models/maintenance-checklist.model';
import { MaintenanceService } from '../../../../Services/maintenance.service';
import { buildServiceReportPdf, ReportInfoEntry, ReportSignature } from '../service-report-pdf.util';

export interface ReportDialogData {
  maintenance: Maintenance;
  customer: Customer | null;
  isPlanned: boolean;
  durationHours?: number | null;
  durationMinutes?: number | null;
  // Service call details
  workItems?: string[];
  description?: string;
  photos?: { dataUrl: string; name: string }[];
  signatureDataUrl?: string | null;
  customerSigneeName?: string;
  arrivalTime?: string | null;
  departureTime?: string | null;
  travelHours?: number | null;
  travelMinutes?: number | null;
  departureFromOffice?: string | null;
  returnToOffice?: string | null;
  initialSituation?: string;
  problems?: any[];
  techSigneeName?: string;
  techSignatureDataUrl?: string | null;
  regieLines?: MaintenanceRegieLine[];
}

export interface GeneratedReportPdf {
  fileName: string;
  pdfBase64: string;
}

export interface ReportDialogResult {
  generatedPdf?: GeneratedReportPdf;
}

@Component({
  selector: 'app-service-report-dialog',
  templateUrl: './service-report-dialog.component.html',
  styleUrls: ['./service-report-dialog.component.css']
})
export class ServiceReportDialogComponent implements AfterViewInit {
  @ViewChild('technicianCanvas') technicianCanvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild('customerCanvas') set customerCanvasSetter(ref: ElementRef<HTMLCanvasElement>) {
    if (ref && ref !== this._customerCanvasRef) {
      this._customerCanvasRef = ref;
      setTimeout(() => {
        this.initCanvas(ref, 'cust');
        this.loadCustomerSignature();
      }, 50);
    }
  }
  private _customerCanvasRef: ElementRef<HTMLCanvasElement> | null = null;

  get customerCanvas(): ElementRef<HTMLCanvasElement> {
    return this._customerCanvasRef!;
  }

  maintenance: Maintenance;
  customer: Customer | null;
  isPlanned: boolean;
  durationHours: number;
  durationMinutes: number;
  travelHours: number;
  travelMinutes: number;

  // Service call data
  workItems: string[];
  description: string;
  scPhotos: { dataUrl: string; name: string }[];
  existingSignatureDataUrl: string | null;
  existingTechSignatureDataUrl: string | null;
  existingCustomerSigneeName: string;
  arrivalTime: string | null;
  departureTime: string | null;

  additionalNotes = '';
  technicianName = '';
  customerSigneeName = '';
  includeCustomerSignature = true;
  savingToServer = false;

  private techCtx: CanvasRenderingContext2D | null = null;
  private custCtx: CanvasRenderingContext2D | null = null;
  private techDrawing = false;
  private custDrawing = false;
  techSigned = false;
  custSigned = false;

  constructor(
    public dialogRef: MatDialogRef<ServiceReportDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ReportDialogData,
    private maintenanceService: MaintenanceService,
    private snackBar: MatSnackBar
  ) {
    this.maintenance = data.maintenance;
    this.customer = data.customer;
    this.isPlanned = data.isPlanned;
    this.durationHours = data.durationHours ?? 0;
    this.durationMinutes = data.durationMinutes ?? 0;
    this.travelHours = data.travelHours ?? 0;
    this.travelMinutes = data.travelMinutes ?? 0;
    this.technicianName = data.techSigneeName || data.maintenance.assignedUser?.userName || data.maintenance.assignedEmployee?.fullName || '';

    // Service call data
    this.workItems = data.workItems ?? [];
    this.description = data.description ?? '';
    this.scPhotos = data.photos ?? [];
    this.existingSignatureDataUrl = data.signatureDataUrl ?? null;
    this.existingTechSignatureDataUrl = data.techSignatureDataUrl ?? null;
    this.existingCustomerSigneeName = data.customerSigneeName ?? '';
    this.arrivalTime = data.arrivalTime ?? null;
    this.departureTime = data.departureTime ?? null;
    if (this.existingCustomerSigneeName) {
      this.customerSigneeName = this.existingCustomerSigneeName;
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.initCanvas(this.technicianCanvas, 'tech');
      this.loadTechnicianSignature();
    }, 100);
  }

  private initCanvas(ref: ElementRef<HTMLCanvasElement>, type: 'tech' | 'cust'): void {
    if (!ref) return;
    const canvas = ref.nativeElement;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = canvas.offsetWidth * 2;
    canvas.height = canvas.offsetHeight * 2;
    ctx.scale(2, 2);
    ctx.strokeStyle = '#1a1a2e';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    if (type === 'tech') this.techCtx = ctx;
    else this.custCtx = ctx;

    canvas.addEventListener('mousedown', (e) => this.startDraw(e, type));
    canvas.addEventListener('mousemove', (e) => this.draw(e, type));
    canvas.addEventListener('mouseup', () => this.endDraw(type));
    canvas.addEventListener('mouseleave', () => this.endDraw(type));

    // Touch support
    canvas.addEventListener('touchstart', (e) => { e.preventDefault(); this.startDrawTouch(e, type); });
    canvas.addEventListener('touchmove', (e) => { e.preventDefault(); this.drawTouch(e, type); });
    canvas.addEventListener('touchend', () => this.endDraw(type));
  }

  private getPos(e: MouseEvent, canvas: HTMLCanvasElement): { x: number; y: number } {
    const rect = canvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  private getTouchPos(e: TouchEvent, canvas: HTMLCanvasElement): { x: number; y: number } {
    const rect = canvas.getBoundingClientRect();
    const touch = e.touches[0];
    return { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
  }

  private startDraw(e: MouseEvent, type: 'tech' | 'cust'): void {
    const ctx = type === 'tech' ? this.techCtx : this.custCtx;
    const canvas = type === 'tech' ? this.technicianCanvas.nativeElement : this.customerCanvas.nativeElement;
    if (!ctx) return;
    if (type === 'tech') this.techDrawing = true; else this.custDrawing = true;
    const pos = this.getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  }

  private draw(e: MouseEvent, type: 'tech' | 'cust'): void {
    const drawing = type === 'tech' ? this.techDrawing : this.custDrawing;
    if (!drawing) return;
    const ctx = type === 'tech' ? this.techCtx : this.custCtx;
    const canvas = type === 'tech' ? this.technicianCanvas.nativeElement : this.customerCanvas.nativeElement;
    if (!ctx) return;
    const pos = this.getPos(e, canvas);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    if (type === 'tech') this.techSigned = true; else this.custSigned = true;
  }

  private startDrawTouch(e: TouchEvent, type: 'tech' | 'cust'): void {
    const ctx = type === 'tech' ? this.techCtx : this.custCtx;
    const canvas = type === 'tech' ? this.technicianCanvas.nativeElement : this.customerCanvas.nativeElement;
    if (!ctx) return;
    if (type === 'tech') this.techDrawing = true; else this.custDrawing = true;
    const pos = this.getTouchPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  }

  private drawTouch(e: TouchEvent, type: 'tech' | 'cust'): void {
    const drawing = type === 'tech' ? this.techDrawing : this.custDrawing;
    if (!drawing) return;
    const ctx = type === 'tech' ? this.techCtx : this.custCtx;
    const canvas = type === 'tech' ? this.technicianCanvas.nativeElement : this.customerCanvas.nativeElement;
    if (!ctx) return;
    const pos = this.getTouchPos(e, canvas);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    if (type === 'tech') this.techSigned = true; else this.custSigned = true;
  }

  private endDraw(type: 'tech' | 'cust'): void {
    if (type === 'tech') this.techDrawing = false; else this.custDrawing = false;
  }

  clearSignature(type: 'tech' | 'cust'): void {
    const ctx = type === 'tech' ? this.techCtx : this.custCtx;
    const canvas = type === 'tech' ? this.technicianCanvas.nativeElement : this.customerCanvas.nativeElement;
    if (!ctx || !canvas) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (type === 'tech') this.techSigned = false; else this.custSigned = false;
  }

  get checklists(): MaintenanceChecklist[] {
    return this.maintenance.checklists ?? [];
  }

  get materials(): MaintenanceMaterial[] {
    return this.maintenance.materials ?? [];
  }

  get regieLines(): MaintenanceRegieLine[] {
    return this.maintenance.regieLines?.length
      ? this.maintenance.regieLines
      : this.data.regieLines ?? [];
  }

  get regieText(): string {
    if (this.regieLines.length === 0) return '–';
    return `${this.getRegieHoursTotal().toLocaleString('de-AT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}h · ${this.getRegieTotal().toLocaleString('de-AT', { style: 'currency', currency: 'EUR' })}`;
  }

  getRegieTotal(): number {
    return this.regieLines.reduce((sum, line) => sum + ((Number(line.hours) || 0) * (Number(line.hourlyRate) || 0)), 0);
  }

  getRegieHoursTotal(): number {
    return this.regieLines.reduce((sum, line) => sum + (Number(line.hours) || 0), 0);
  }

  get totalItems(): number {
    return this.checklists.reduce((s, cl) => s + (cl.items?.length ?? 0), 0);
  }

  get completedItems(): number {
    // "Nicht OK" zählt als geprüft/bearbeitet.
    return this.checklists.reduce((s, cl) => s + ((cl.items ?? []).filter((i: any) => i.isChecked || i.isNotOk).length), 0);
  }

  get durationText(): string {
    const h = this.durationHours || 0;
    const m = this.durationMinutes || 0;
    if (h === 0 && m === 0) return '–';
    return `${h}h ${m}min`;
  }

  get travelTimeText(): string {
    const h = this.travelHours || 0;
    const m = this.travelMinutes || 0;
    if (h === 0 && m === 0) return '–';
    return `${h}h ${m}min`;
  }

  async generatePDF(): Promise<void> {
    // Save signatures before generating PDF
    this.saveSignaturesOnExport();
    this.persistSignaturesToMaintenance();
    try {
      // Maßgeblich ist durchgehend das Einsatzdatum (scheduledDate), NICHT der
      // Abschluss- oder Erstellungszeitpunkt: der Bericht dokumentiert den Einsatz
      // an diesem Tag. Wird der Bericht später erstellt oder neu erzeugt, darf sich
      // das Datum im PDF dadurch nicht verschieben.
      const einsatzDatum = new Date(this.maintenance.scheduledDate);
      const einsatzDatumStr = einsatzDatum.toLocaleDateString('de-AT');

      const info: ReportInfoEntry[] = [
        { label: 'Kunde', value: this.customer?.name ?? '–' },
        { label: 'Adresse', value: this.customer?.address ?? '–' },
        { label: 'Auftrag', value: this.maintenance.title },
        { label: 'Anlage', value: this.maintenance.equipment?.name ?? '–' },
        { label: 'Datum', value: einsatzDatumStr },
        { label: 'Techniker', value: this.technicianName || '–' }
      ];
      if (!this.isPlanned) {
        info.push({ label: 'Arbeitszeit', value: this.durationText });
        info.push({ label: 'Fahrzeit', value: this.travelTimeText });
        if (this.regieLines.length > 0) info.push({ label: 'Regie', value: this.regieText });
        if (this.arrivalTime) info.push({ label: 'Ankunft', value: this.arrivalTime });
        if (this.departureTime) info.push({ label: 'Abreise', value: this.departureTime });
      }

      const photos: string[] = [];
      for (const photo of this.scPhotos) {
        try {
          photos.push(await this.resolvePhotoDataUrl(photo.dataUrl));
        } catch { /* Foto nicht ladbar – überspringen */ }
      }

      const techImg = this.techSigned && this.technicianCanvas
        ? this.technicianCanvas.nativeElement.toDataURL('image/png')
        : this.existingTechSignatureDataUrl;
      const custImg = this.custSigned && this._customerCanvasRef
        ? this.customerCanvas.nativeElement.toDataURL('image/png')
        : this.existingSignatureDataUrl;
      const signatures: ReportSignature[] = [
        { label: 'Techniker', name: this.technicianName, imageDataUrl: techImg }
      ];
      if (this.includeCustomerSignature) {
        signatures.push({
          label: 'Kunde',
          name: this.customerSigneeName || this.existingCustomerSigneeName,
          imageDataUrl: custImg
        });
      }

      const doc = await buildServiceReportPdf({
        title: this.isPlanned ? 'Wartungs-Report' : 'Einsatz-Report',
        subline: `${this.isPlanned ? 'Wartungsdatum' : 'Einsatzdatum'}: ${einsatzDatumStr}`,
        info,
        initialSituation: !this.isPlanned ? (this.data.initialSituation ?? null) : null,
        problems: !this.isPlanned
          ? (this.data.problems ?? []).map((p: any) => ({ description: p?.description ?? '', solution: p?.solution ?? '' }))
          : [],
        workDescription: !this.isPlanned ? this.description : null,
        workItems: !this.isPlanned ? this.workItems : [],
        checklists: this.checklists.map(cl => ({
          name: cl.name || 'Checkliste',
          items: (cl.items ?? []).map(item => ({
            description: item.description,
            isChecked: item.isChecked,
            isNotOk: item.isNotOk ?? false,
            remarks: item.remarks,
            comments: (item.comments ?? []).map(c => c.createdByName ? `${c.text} – ${c.createdByName}` : c.text)
          }))
        })),
        materials: this.materials.map(m => ({ name: m.name, quantity: m.quantity, unit: m.unit })),
        regieLines: this.regieLines.map(l => ({
          label: l.label,
          hours: Number(l.hours) || 0,
          hourlyRate: Number(l.hourlyRate) || 0
        })),
        photos,
        additionalNotes: this.additionalNotes,
        signatures
      });

      // Save
      // Datum aus den lokalen Bestandteilen bilden, nicht über toISOString():
      // das rechnet nach UTC um und kann den Tag verschieben.
      const jahr = einsatzDatum.getFullYear();
      const monat = `${einsatzDatum.getMonth() + 1}`.padStart(2, '0');
      const tag = `${einsatzDatum.getDate()}`.padStart(2, '0');
      const filename = `${this.isPlanned ? 'Wartung' : 'Einsatz'}_${this.customer?.name ?? 'Report'}_${jahr}-${monat}-${tag}.pdf`;
      const pdfDataUri = doc.output('datauristring');
      const pdfBase64 = pdfDataUri.includes(',') ? pdfDataUri.split(',')[1] : pdfDataUri;
      doc.save(filename);

      // Auto-save to server
      if (this.maintenance?.id) {
        this.savingToServer = true;
        this.maintenanceService.uploadServiceCallReportPdf(this.maintenance.id, {
          fileName: filename,
          pdfBase64
        }).subscribe({
          next: () => {
            this.savingToServer = false;
            this.snackBar.open('PDF am Server gespeichert', 'OK', { duration: 2500 });
            this.dialogRef.close({
              generatedPdf: { fileName: filename, pdfBase64 }
            } as ReportDialogResult);
          },
          error: () => {
            this.savingToServer = false;
            this.snackBar.open('Server-Speicherung fehlgeschlagen – PDF nur lokal', 'OK', { duration: 3500 });
            this.dialogRef.close({
              generatedPdf: { fileName: filename, pdfBase64 }
            } as ReportDialogResult);
          }
        });
      } else {
        this.dialogRef.close({
          generatedPdf: { fileName: filename, pdfBase64 }
        } as ReportDialogResult);
      }
    } catch (e) {
      console.error('PDF generation error:', e);
    }
  }

  close(): void {
    this.dialogRef.close();
  }

  // --- Signature Persistence ---

  /** Save customer signature to this service call's localStorage, and technician signature globally */
  private saveSignaturesOnExport(): void {
    const customerAcknowledged = this.isCustomerAcknowledgedForExport();
    // Save customer signature to service-call localStorage
    if (!this.isPlanned && this.maintenance?.id) {
      try {
        const key = `service-call-${this.maintenance.id}`;
        const raw = localStorage.getItem(key);
        const data = raw ? JSON.parse(raw) : {};
        if (this.custSigned && this.customerCanvas) {
          data.signatureDataUrl = this.customerCanvas.nativeElement.toDataURL('image/png');
        }
        if (this.customerSigneeName) {
          data.customerSigneeName = this.customerSigneeName;
        }
        data.regieLines = this.getRegieLinesForPersistence(customerAcknowledged);
        localStorage.setItem(key, JSON.stringify(data));
      } catch (e) {
        console.warn('Could not save customer signature to localStorage:', e);
      }
    }

    // Save technician signature globally
    this.saveTechnicianSignature();
  }

  private persistSignaturesToMaintenance(): void {
    if (!this.maintenance?.id || this.isPlanned) return;

    const techSignatureDataUrl = this.techSigned && this.technicianCanvas
      ? this.technicianCanvas.nativeElement.toDataURL('image/png')
      : this.existingTechSignatureDataUrl;
    const customerSignatureDataUrl = this.custSigned && this.customerCanvas
      ? this.customerCanvas.nativeElement.toDataURL('image/png')
      : this.existingSignatureDataUrl;
    const customerAcknowledged = this.isCustomerAcknowledgedForExport(customerSignatureDataUrl);
    const regieLines = this.getRegieLinesForPersistence(customerAcknowledged);

    this.maintenanceService.updateMaintenance(this.maintenance.id, {
      ...this.maintenance,
      techSigneeName: this.technicianName || this.maintenance.techSigneeName || null,
      techSignatureDataUrl: techSignatureDataUrl ?? this.maintenance.techSignatureDataUrl ?? null,
      customerSigneeName: this.customerSigneeName || this.existingCustomerSigneeName || this.maintenance.customerSigneeName || null,
      customerSignatureDataUrl: customerSignatureDataUrl ?? this.maintenance.customerSignatureDataUrl ?? null,
      customerSignatureEnabled: this.includeCustomerSignature,
      customerAcknowledged,
      regieLines
    }).subscribe({
      next: (updated) => {
        this.maintenance = updated;
        this.existingTechSignatureDataUrl = updated.techSignatureDataUrl ?? this.existingTechSignatureDataUrl;
        this.existingSignatureDataUrl = updated.customerSignatureDataUrl ?? this.existingSignatureDataUrl;
      },
      error: () => {
        console.warn('Could not persist report signatures to maintenance');
      }
    });
  }

  private isCustomerAcknowledgedForExport(customerSignatureDataUrl: string | null | undefined = undefined): boolean {
    const signature = customerSignatureDataUrl === undefined
      ? (this.custSigned && this.customerCanvas ? this.customerCanvas.nativeElement.toDataURL('image/png') : this.existingSignatureDataUrl)
      : customerSignatureDataUrl;

    return this.includeCustomerSignature && (!!signature || !!this.customerSigneeName || !!this.existingCustomerSigneeName);
  }

  private getRegieLinesForPersistence(customerAcknowledged: boolean): MaintenanceRegieLine[] {
    return this.regieLines.map(line => ({
      ...line,
      isCustomerAcknowledged: customerAcknowledged ? true : !!line.isCustomerAcknowledged
    }));
  }

  /** Save technician signature with their userId as key */
  private saveTechnicianSignature(): void {
    const userId = this.maintenance?.assignedUserId;
    if (!userId || !this.techSigned || !this.technicianCanvas) return;
    try {
      const sigData = {
        name: this.technicianName,
        signatureDataUrl: this.technicianCanvas.nativeElement.toDataURL('image/png')
      };
      localStorage.setItem(`technician-sig-${userId}`, JSON.stringify(sigData));
    } catch (e) {
      console.warn('Could not save technician signature:', e);
    }
  }

  /** Load previously saved technician signature and draw on canvas */
  private loadTechnicianSignature(): void {
    const userId = this.maintenance?.assignedUserId;
    if (!userId) return;
    try {
      const raw = localStorage.getItem(`technician-sig-${userId}`);
      if (!raw) return;
      const sigData = JSON.parse(raw);
      if (sigData.signatureDataUrl && this.technicianCanvas) {
        const canvas = this.technicianCanvas.nativeElement;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        const img = new Image();
        img.onload = () => {
          ctx.drawImage(img, 0, 0, canvas.offsetWidth, canvas.offsetHeight);
          this.techSigned = true;
        };
        img.src = sigData.signatureDataUrl;
      }
    } catch (e) {
      console.warn('Could not load technician signature:', e);
    }
  }

  /** Load previously saved customer signature and draw on canvas */
  private loadCustomerSignature(): void {
    if (this.isPlanned || !this.maintenance?.id) return;
    try {
      const raw = localStorage.getItem(`service-call-${this.maintenance.id}`);
      if (!raw) return;
      const data = JSON.parse(raw);
      if (data.signatureDataUrl && this.customerCanvas) {
        const canvas = this.customerCanvas.nativeElement;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        const img = new Image();
        img.onload = () => {
          ctx.drawImage(img, 0, 0, canvas.offsetWidth, canvas.offsetHeight);
          this.custSigned = true;
        };
        img.src = data.signatureDataUrl;
      }
      if (data.customerSigneeName && !this.customerSigneeName) {
        this.customerSigneeName = data.customerSigneeName;
      }
    } catch (e) {
      console.warn('Could not load customer signature:', e);
    }
  }

  private async resolvePhotoDataUrl(source: string): Promise<string> {
    if (!source) {
      throw new Error('Empty photo source');
    }

    if (source.startsWith('data:')) {
      return source;
    }

    const response = await fetch(source);
    if (!response.ok) {
      throw new Error('Photo could not be loaded');
    }

    const blob = await response.blob();
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error('Photo conversion failed'));
      reader.readAsDataURL(blob);
    });
  }
}
